import React, { useState, useEffect } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { usePageTitle } from '@/stores/page-title-store'
import ProtectedRoute from '@/components/ProtectedRoute'
import {
  FileText,
  Download,
  Eye,
  Search,
  Filter,
  Calendar,
  CheckCircle,
  Clock,
  AlertTriangle,
  Upload,
  Folder,
  File,
  PaperclipIcon
} from 'lucide-react'

interface Document {
  id: string
  name: string
  type: 'contract' | 'payslip' | 'certificate' | 'policy' | 'form' | 'other'
  category: string
  uploadDate: string
  size: string
  status: 'available' | 'pending' | 'expired'
  url?: string
}

// Mock documents - in production, fetch from API
const mockDocuments: Document[] = [
  {
    id: '1',
    name: 'Contrato de Trabalho - 2024.pdf',
    type: 'contract',
    category: 'Contratos',
    uploadDate: '2024-01-15',
    size: '2.4 MB',
    status: 'available',
    url: '#'
  },
  {
    id: '2',
    name: 'Holerite - Dezembro 2024.pdf',
    type: 'payslip',
    category: 'Folha de Pagamento',
    uploadDate: '2024-12-05',
    size: '156 KB',
    status: 'available',
    url: '#'
  },
  {
    id: '3',
    name: 'Holerite - Novembro 2024.pdf',
    type: 'payslip',
    category: 'Folha de Pagamento',
    uploadDate: '2024-11-05',
    size: '152 KB',
    status: 'available',
    url: '#'
  },
  {
    id: '4',
    name: 'Certificado - Treinamento Segurança.pdf',
    type: 'certificate',
    category: 'Certificados',
    uploadDate: '2024-10-20',
    size: '890 KB',
    status: 'available',
    url: '#'
  },
  {
    id: '5',
    name: 'Política de Benefícios 2024.pdf',
    type: 'policy',
    category: 'Políticas',
    uploadDate: '2024-01-01',
    size: '1.2 MB',
    status: 'available',
    url: '#'
  },
  {
    id: '6',
    name: 'Declaração de IR - Pendente',
    type: 'form',
    category: 'Formulários',
    uploadDate: '2024-12-10',
    size: '-',
    status: 'pending',
    url: '#'
  }
]

const documentTypeConfig = {
  contract: { label: 'Contrato', icon: FileText, color: 'text-brand-primary' },
  payslip: { label: 'Holerite', icon: File, color: 'text-success' },
  certificate: { label: 'Certificado', icon: CheckCircle, color: 'text-brand-blue' },
  policy: { label: 'Política', icon: Folder, color: 'text-warning' },
  form: { label: 'Formulário', icon: PaperclipIcon, color: 'text-brand-gray' },
  other: { label: 'Outro', icon: FileText, color: 'text-brand-gray-light' }
}

function DocumentsContent() {
  const { user } = useAuth()
  usePageTitle('Documentos')
  const [documents, setDocuments] = useState<Document[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState<string>('all')
  const [selectedStatus, setSelectedStatus] = useState<string>('all')

  useEffect(() => {
    // Simulate loading documents
    const loadDocuments = async () => {
      setIsLoading(true)
      // In production, fetch from API
      setTimeout(() => {
        setDocuments(mockDocuments)
        setIsLoading(false)
      }, 600)
    }

    loadDocuments()
  }, [user])

  // Filter documents
  const filteredDocuments = documents.filter(doc => {
    const matchesSearch = doc.name.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesCategory = selectedCategory === 'all' || doc.category === selectedCategory
    const matchesStatus = selectedStatus === 'all' || doc.status === selectedStatus

    return matchesSearch && matchesCategory && matchesStatus
  })

  // Get unique categories
  const categories = ['all', ...Array.from(new Set(documents.map(d => d.category)))]

  const documentStats = {
    total: documents.length,
    available: documents.filter(d => d.status === 'available').length,
    pending: documents.filter(d => d.status === 'pending').length,
    expired: documents.filter(d => d.status === 'expired').length
  }

  const handleDownload = (doc: Document) => {
    // In production, trigger actual download
  }

  const handleView = (doc: Document) => {
    // In production, open document viewer
  }

  if (isLoading) {
    return (
      
        <div className="flex justify-center items-center min-h-[400px]">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-brand-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-brand-gray-light font-body">Carregando documentos...</p>
          </div>
        </div>
      
    )
  }

  return (
    
      <div className="space-y-8 animate-fade-in-up">
        {/* Header */}
        <div className="flex justify-end">
          <button className="btn-primary flex items-center gap-2">
            <Upload size={20} />
            Enviar Documento
          </button>
        </div>

        {/* Stats Cards */}
        <div className="dashboard-grid lg:grid-cols-4">
          <div className="card-hover">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 rounded-monumenta bg-brand-primary/10">
                <FileText className="w-6 h-6 text-brand-primary" />
              </div>
            </div>
            <div className="space-y-2">
              <div className="text-3xl font-display font-bold text-white">
                {documentStats.total}
              </div>
              <p className="text-sm text-brand-gray">
                Total de Documentos
              </p>
            </div>
          </div>

          <div className="card-hover">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 rounded-monumenta bg-success/10">
                <CheckCircle className="w-6 h-6 text-success" />
              </div>
            </div>
            <div className="space-y-2">
              <div className="text-3xl font-display font-bold text-white">
                {documentStats.available}
              </div>
              <p className="text-sm text-brand-gray">
                Disponíveis
              </p>
            </div>
          </div>

          <div className="card-hover">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 rounded-monumenta bg-warning/10">
                <Clock className="w-6 h-6 text-warning" />
              </div>
            </div>
            <div className="space-y-2">
              <div className="text-3xl font-display font-bold text-white">
                {documentStats.pending}
              </div>
              <p className="text-sm text-brand-gray">
                Pendentes
              </p>
            </div>
          </div>

          <div className="card-hover">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 rounded-monumenta bg-error/10">
                <AlertTriangle className="w-6 h-6 text-error" />
              </div>
            </div>
            <div className="space-y-2">
              <div className="text-3xl font-display font-bold text-white">
                {documentStats.expired}
              </div>
              <p className="text-sm text-brand-gray">
                Vencidos
              </p>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="card">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-brand-gray w-5 h-5" />
              <input
                type="text"
                placeholder="Buscar documentos..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input-field pl-10"
              />
            </div>

            {/* Category Filter */}
            <div className="lg:w-64">
              <div className="relative">
                <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-brand-gray w-5 h-5" />
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="input-field pl-10 appearance-none"
                >
                  <option value="all">Todas as Categorias</option>
                  {categories.filter(c => c !== 'all').map(category => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Status Filter */}
            <div className="lg:w-48">
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="input-field appearance-none"
              >
                <option value="all">Todos os Status</option>
                <option value="available">Disponível</option>
                <option value="pending">Pendente</option>
                <option value="expired">Vencido</option>
              </select>
            </div>
          </div>
        </div>

        {/* Documents List */}
        <div className="card">
          <h2 className="font-display text-2xl font-bold text-white mb-6">
            Documentos ({filteredDocuments.length})
          </h2>

          {filteredDocuments.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-16 h-16 text-brand-gray mx-auto mb-4" />
              <h3 className="font-display text-xl font-bold text-white mb-2">
                Nenhum documento encontrado
              </h3>
              <p className="text-brand-gray-light">
                {searchTerm || selectedCategory !== 'all' || selectedStatus !== 'all'
                  ? 'Tente ajustar os filtros de busca'
                  : 'Você ainda não possui documentos cadastrados'}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredDocuments.map(doc => {
                const config = documentTypeConfig[doc.type]
                const Icon = config.icon

                return (
                  <div
                    key={doc.id}
                    className="card-interactive p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4 hover:border-brand-primary/30 transition-all duration-200"
                  >
                    <div className="flex items-start gap-4 flex-1">
                      <div className={`p-3 rounded-monumenta bg-brand-dark-lighter ${config.color}`}>
                        <Icon className="w-5 h-5" />
                      </div>

                      <div className="flex-1 min-w-0">
                        <h3 className="font-display font-bold text-white mb-1 truncate">
                          {doc.name}
                        </h3>
                        <div className="flex flex-wrap items-center gap-3 text-sm text-brand-gray">
                          <span className="flex items-center gap-1">
                            <Folder className="w-4 h-4" />
                            {doc.category}
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {new Date(doc.uploadDate).toLocaleDateString('pt-BR')}
                          </span>
                          {doc.size !== '-' && (
                            <span>{doc.size}</span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      {/* Status Badge */}
                      <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                        doc.status === 'available' ? 'bg-success/20 text-success' :
                        doc.status === 'pending' ? 'bg-warning/20 text-warning' :
                        'bg-error/20 text-error'
                      }`}>
                        {doc.status === 'available' ? 'Disponível' :
                         doc.status === 'pending' ? 'Pendente' : 'Vencido'}
                      </div>

                      {/* Actions */}
                      {doc.status === 'available' && (
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleView(doc)}
                            className="p-2 rounded-monumenta bg-brand-dark-lighter hover:bg-brand-primary/20 text-brand-gray hover:text-brand-primary transition-all duration-200"
                            title="Visualizar"
                          >
                            <Eye className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => handleDownload(doc)}
                            className="p-2 rounded-monumenta bg-brand-dark-lighter hover:bg-success/20 text-brand-gray hover:text-success transition-all duration-200"
                            title="Baixar"
                          >
                            <Download className="w-5 h-5" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>

        {/* Help Section */}
        <div className="card bg-brand-blue/5 border-brand-blue/20">
          <div className="flex items-start gap-4">
            <div className="p-3 rounded-monumenta bg-brand-blue/20">
              <AlertTriangle className="w-6 h-6 text-brand-blue" />
            </div>
            <div>
              <h3 className="font-display text-lg font-bold text-white mb-2">
                Precisa de Ajuda?
              </h3>
              <p className="text-brand-gray-light mb-4">
                Se você não encontrou o documento que procura ou precisa de assistência, entre em contato com o RH.
              </p>
              <button className="btn-secondary text-sm">
                Contatar RH
              </button>
            </div>
          </div>
        </div>
      </div>
    
  )
}

export default function DocumentsPage() {
  return (
    <ProtectedRoute>
      <DocumentsContent />
    </ProtectedRoute>
  )
}
